import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBracketsCurly } from "@fortawesome/pro-regular-svg-icons";
import Portal from "elements/Portal";
import Input from "components/form/input/Input";
import {
  MERGE_TAG_CATALOG,
  MERGE_TAG_KEYS,
  mergeTagLabel,
  wrapMergeTag,
  wrapMergeTagForLinkUrl,
  type MergeTagKey,
} from "../domain/merge-tags";
import {
  EMAIL_BUILDER_SETTINGS_DEFAULT_OVERLAY_PORTAL_ID,
  useEmailBuilderSettingsOverlayPortalId,
} from "../shell/canvas/settings/emailBuilderSettingsOverlayPortal";
import { useStableScopedDomId } from "../useStableDomId";
import {
  focusEmailBuilderFieldMergeTagInputCaret,
  insertEmailBuilderFieldMergeTagAtSelection,
} from "./emailBuilderFieldMergeTagInsert";

const POPOVER_WIDTH_PX = 280;
const POPOVER_GAP_PX = 6;
const POPOVER_MAX_HEIGHT_PX = 320;
const POPOVER_EDGE_GAP_PX = 8;
const POPOVER_PANEL_INSET_PX = 12;

function resolvePopoverBounds(trigger: HTMLElement): {
  minLeft: number;
  maxLeft: number;
  maxWidth: number;
  boundsRight: number;
} {
  const viewportLeft = POPOVER_EDGE_GAP_PX;
  const viewportRight = window.innerWidth - POPOVER_PANEL_INSET_PX;
  let boundsLeft = viewportLeft;
  let boundsRight = viewportRight;

  const panel =
    trigger.closest(".email-builder-settings-side-panel--right") ??
    trigger.closest(".email-builder-settings-bottom-sheet-content") ??
    trigger.closest(".email-builder-settings-bottom-sheet-body");

  if (panel instanceof HTMLElement) {
    const panelRect = panel.getBoundingClientRect();
    boundsLeft = Math.max(boundsLeft, panelRect.left + POPOVER_PANEL_INSET_PX);
    boundsRight = Math.min(boundsRight, panelRect.right - POPOVER_PANEL_INSET_PX);
  }

  const availableWidth = Math.max(0, boundsRight - boundsLeft);
  const maxWidth = Math.min(POPOVER_WIDTH_PX, availableWidth);
  const minLeft = boundsLeft;
  const maxLeft = Math.max(minLeft, boundsRight - maxWidth);

  return { minLeft, maxLeft, maxWidth, boundsRight };
}

type TEmailBuilderFieldMergeTagPickerVariant = "default" | "link";

type EmailBuilderFieldMergeTagPickerProps = {
  value: string;
  onInsert: (nextValue: string) => void;
  allowedKeys?: readonly MergeTagKey[];
  variant?: TEmailBuilderFieldMergeTagPickerVariant;
};

export function EmailBuilderFieldMergeTagPicker({
  value,
  onInsert,
  allowedKeys = MERGE_TAG_KEYS,
  variant = "default",
}: EmailBuilderFieldMergeTagPickerProps) {
  const { t } = useTranslation();
  const portalId = useEmailBuilderSettingsOverlayPortalId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipId = useStableScopedDomId("email-builder-field-merge-tag-picker", "trigger");
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
    maxWidth: number;
    maxHeight: number;
    positionMode: "fixed" | "absolute";
  } | null>(null);

  const groups = useMemo(() => {
    const allowedKeySet = new Set<string>(allowedKeys);
    const needle = query.trim().toLowerCase();
    const result: typeof MERGE_TAG_CATALOG = [];
    for (const group of MERGE_TAG_CATALOG) {
      const allowedTags = group.tags.filter(tag => allowedKeySet.has(tag.key));
      const tags = needle
        ? allowedTags.filter(tag => {
            const mergeTag = tag.key;
            const label = mergeTagLabel(mergeTag);
            return (
              tag.key.toLowerCase().includes(needle) ||
              label.toLowerCase().includes(needle) ||
              group.category.toLowerCase().includes(needle)
            );
          })
        : allowedTags;
      if (tags.length > 0) {
        result.push({ ...group, tags });
      }
    }
    return result;
  }, [allowedKeys, query]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const { minLeft, maxLeft, maxWidth, boundsRight } = resolvePopoverBounds(trigger);

    const popoverRight = Math.min(rect.right, boundsRight);
    let left = popoverRight - maxWidth;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    const spaceBelow = Math.max(
      0,
      window.innerHeight - POPOVER_EDGE_GAP_PX - (rect.bottom + POPOVER_GAP_PX),
    );
    const spaceAbove = Math.max(0, rect.top - POPOVER_GAP_PX - POPOVER_EDGE_GAP_PX);
    const placeBelow =
      spaceBelow >= Math.min(POPOVER_MAX_HEIGHT_PX, 120) || spaceBelow >= spaceAbove;
    const maxHeight = placeBelow
      ? Math.min(POPOVER_MAX_HEIGHT_PX, spaceBelow)
      : Math.min(POPOVER_MAX_HEIGHT_PX, spaceAbove);
    const top = placeBelow
      ? rect.bottom + POPOVER_GAP_PX
      : Math.max(POPOVER_EDGE_GAP_PX, rect.top - POPOVER_GAP_PX - maxHeight);

    let positionMode: "fixed" | "absolute" = "fixed";
    let resolvedLeft = left;
    let resolvedTop = top;

    if (portalId !== EMAIL_BUILDER_SETTINGS_DEFAULT_OVERLAY_PORTAL_ID) {
      const portalMount = document.getElementById(portalId);
      if (portalMount) {
        const mountRect = portalMount.getBoundingClientRect();
        resolvedLeft -= mountRect.left;
        resolvedTop -= mountRect.top;
        positionMode = "absolute";
      }
    }

    setPosition({
      left: resolvedLeft,
      top: resolvedTop,
      maxWidth,
      maxHeight,
      positionMode,
    });
  }, [portalId]);

  useEffect(() => {
    if (!open) {
      return;
    }
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open || !position) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus({ preventScroll: true });
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [open, position]);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onPointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      close();
    }
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [open, close]);

  function handleInsert(key: MergeTagKey) {
    const token = variant === "link" ? wrapMergeTagForLinkUrl(key) : wrapMergeTag(key);
    const { nextValue, caret } = insertEmailBuilderFieldMergeTagAtSelection({
      trigger: triggerRef.current,
      value,
      token,
    });
    onInsert(nextValue);
    focusEmailBuilderFieldMergeTagInputCaret(triggerRef.current, caret);
    close();
  }

  const tooltipLabel = t("Merge tags");

  const triggerButton = (
    <span className={"email-builder-merge-tag-trigger-wrapper"}>
      <button
        ref={triggerRef}
        type="button"
        className={"email-builder-merge-tag-trigger"}
        aria-label={t("Insert merge tag")}
        aria-expanded={open}
        aria-haspopup="listbox"
        onMouseDown={event => event.preventDefault()}
        onClick={() => {
          if (open) {
            close();
            return;
          }
          setOpen(true);
        }}
      >
        <FontAwesomeIcon icon={faBracketsCurly} aria-hidden="true" />
      </button>
    </span>
  );

  return (
    <>
      {open ? (
        triggerButton
      ) : (
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 400, hide: 100 }}
          trigger={["hover", "focus"]}
          overlay={<Tooltip id={tooltipId}>{tooltipLabel}</Tooltip>}
        >
          {triggerButton}
        </OverlayTrigger>
      )}
      {open && position ? (
        <Portal isMounted={true} parentId={portalId}>
          <div
            ref={popoverRef}
            className={"email-builder-merge-tag-popover"}
            data-email-builder-field-merge-tag-popover=""
            aria-label={tooltipLabel}
            style={{
              position: position.positionMode,
              left: position.left,
              top: position.top,
              width: position.maxWidth,
              maxHeight: position.maxHeight,
            }}
            onMouseDown={event => event.preventDefault()}
          >
            <div className={"email-builder-merge-tag-search"}>
              <Input
                inputRef={searchInputRef}
                autoFocus
                label={t("Search tags")}
                labelHidden
                value={query}
                placeholder={t("Search tags…")}
                onChange={event => setQuery(event.target.value)}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === "Escape") {
                    event.preventDefault();
                    close();
                  }
                }}
              />
            </div>
            <div className={"email-builder-merge-tag-list"} role="listbox" aria-label={tooltipLabel}>
              {groups.length === 0 ? (
                <p className={"email-builder-merge-tag-empty"}>{t("No matching tags")}</p>
              ) : (
                groups.map(group => (
                  <section key={group.category} className={"email-builder-merge-tag-group"}>
                    <h3 className={"email-builder-merge-tag-group-label"}>{t(group.category)}</h3>
                    <ul className={"email-builder-merge-tag-group-list"}>
                      {group.tags.map(tag => {
                        const mergeTag = tag.key;
                        const label = mergeTagLabel(mergeTag);
                        return (
                          <li key={tag.key}>
                            <button
                              type="button"
                              className={"email-builder-merge-tag-option"}
                              role="option"
                              aria-selected={false}
                              onMouseDown={event => event.preventDefault()}
                              onClick={() => handleInsert(mergeTag)}
                            >
                              <span className={"email-builder-merge-tag-option-label"}>{t(label)}</span>
                              <span className={"email-builder-merge-tag-option-tag"}>
                                {variant === "link"
                                  ? wrapMergeTagForLinkUrl(mergeTag)
                                  : wrapMergeTag(mergeTag)}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ))
              )}
            </div>
          </div>
        </Portal>
      ) : null}
    </>
  );
}
